import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../../utils/bookingApi';
import { formatPence } from '../../../utils/money';
import { useAuth } from '../../../contexts/AuthContext';
import {
  PageHeader, Sheet, Skeleton, EmptyState, ErrorState, InlineError, useConfirm, useToast,
} from '../../../components/ui';
import { invoiceStatus, formatDay } from './AdminInvoices';
import { SKIPPED } from './AdminInvoiceDetail';
import CreditNoteForm from './CreditNoteForm';
import '../booking-shared.css';
import './AdminInvoices.css';

const EMPTY = { name: '', contactEmail: '', address: '', vatNumber: '' };
const toForm = (initial) => ({ ...EMPTY, ...Object.fromEntries(Object.entries(initial || {}).map(([k, v]) => [k, v ?? ''])) });

// Adds or edits a business contact in a pop-up. onSave(data) returns a
// promise; a rejection's message shows in the pop-up.
export function ContactSheet({ title, initial, onSave, onClose }) {
  const start = toForm(initial);
  const [form, setForm] = useState(start);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const dirty = Object.keys(EMPTY).some(k => form[k] !== start[k]);

  const submit = async () => {
    if (!form.name.trim()) { setError('Enter a name.'); return; }
    setError(null);
    setSaving(true);
    try {
      await onSave({ name: form.name, contactEmail: form.contactEmail, address: form.address, vatNumber: form.vatNumber });
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't save the contact.");
      setSaving(false);
    }
  };

  return (
    <Sheet
      title={title}
      onClose={onClose}
      dirty={dirty}
      onSubmit={submit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save contact'}</button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="inv-form">
        <label className="bk-label">Name<input className="bk-input" value={form.name} onChange={set('name')} required /></label>
        <label className="bk-label">Email <span className="bk-optional">(optional)</span>
          <input type="email" className="bk-input" value={form.contactEmail} onChange={set('contactEmail')} placeholder="Where invoices are sent" />
        </label>
        <label className="bk-label">Address <span className="bk-optional">(optional)</span>
          <textarea className="bk-input" rows={3} value={form.address} onChange={set('address')} />
        </label>
        <label className="bk-label">VAT number <span className="bk-optional">(optional)</span>
          <input className="bk-input" value={form.vatNumber} onChange={set('vatNumber')} />
        </label>
        {error && <p className="bk-form-error" role="alert">{error}</p>}
      </div>
    </Sheet>
  );
}

function ContactDetail({ id, onChanged }) {
  const [contact, setContact] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(false);
  const [crediting, setCrediting] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();
  // Changes, credit notes and new invoices need Manage access to business
  const { can } = useAuth();
  const managesBusiness = can('business', 'MANAGE');

  const loadContact = useCallback(() => {
    setLoadError(false);
    return bookingApi.getBusinessContact(id)
      .then(res => setContact(res.data))
      .catch(() => setLoadError(true));
  }, [id]);
  useEffect(() => { loadContact(); }, [loadContact]);

  if (loadError && !contact) return <div className="inv-expand"><InlineError onRetry={loadContact}>Couldn't load this contact.</InlineError></div>;
  if (!contact) return <div className="inv-expand"><Skeleton rows={2} label="Loading contact…" /></div>;

  const save = async (data) => {
    await bookingApi.updateBusinessContact(id, data);
    setEditing(false);
    toast('Contact saved');
    await loadContact();
    onChanged();
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${contact.name}?`,
      message: 'The contact goes for good.',
      confirmLabel: 'Delete contact',
    });
    if (!ok) return;
    setError(null);
    try {
      await bookingApi.deleteBusinessContact(id);
      toast('Contact deleted');
      onChanged(true);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't delete the contact.");
    }
  };

  const download = (n) => {
    setError(null);
    bookingApi.downloadCreditNotePdf(n.id, `${n.creditNoteNumber}.pdf`).catch(() => setError(`Couldn't download ${n.creditNoteNumber}. Try again.`));
  };

  return (
    <div className="inv-expand">
      <dl className="inv-details">
        <dt>Email</dt><dd>{contact.contactEmail || 'None'}</dd>
        <dt>Address</dt><dd>{contact.address || 'None'}</dd>
        <dt>VAT number</dt><dd>{contact.vatNumber || 'None'}</dd>
      </dl>
      {managesBusiness && (
        <button type="button" className="bk-btn bk-btn--sm inv-self-start" onClick={() => setEditing(true)}>Edit details</button>
      )}
      {error && <p className="bk-form-error" role="alert">{error}</p>}
      {notice && <p className="inv-note" role="status">{notice}</p>}

      <h3 className="bk-section-title">Credit on account</h3>
      <strong>{formatPence(contact.creditBalance || 0)}</strong>
      {(contact.creditNotes || []).map(n => (
        <button key={n.id} type="button" className="inv-row" style={{ padding: '0.5rem 0' }} onClick={() => download(n)}>
          <span className="inv-row__main">
            <span className="inv-row__name">{n.creditNoteNumber}</span>
            <span className="inv-row__sub">{formatDay(n.issueDate)}{n.available === 0 ? ' · Used' : n.available < n.total ? ` · ${formatPence(n.available)} left` : ''}</span>
          </span>
          <span className="inv-row__amt">{formatPence(n.total)}</span>
        </button>
      ))}
      {managesBusiness && (
        <button type="button" className="bk-btn bk-btn--sm inv-self-start" onClick={() => setCrediting(true)}>New credit note</button>
      )}

      <h3 className="bk-section-title">Invoices</h3>
      {contact.invoices.length === 0 && <span className="inv-muted inv-small">No invoices yet.</span>}
      {contact.invoices.map(inv => {
        const s = invoiceStatus(inv);
        return (
          <Link key={inv.id} to={`/booking/admin/invoices/${inv.id}`} className="inv-row" style={{ padding: '0.5rem 0' }}>
            <span className="inv-row__main"><span className="inv-row__name">{inv.invoiceNumber || 'Draft'}</span></span>
            <span className={`bk-pill bk-pill--${s.tone}`}>{s.label}</span>
            <span className="inv-row__amt">{formatPence(inv.total)}</span>
          </Link>
        );
      })}
      {managesBusiness && (
        <div className="bk-actions">
          <Link className="bk-btn" to={`/booking/admin/invoices/new?contact=${contact.id}`}>New invoice for {contact.name}</Link>
          {contact.invoices.length === 0 && (
            <button type="button" className="bk-btn bk-btn--outline-danger" onClick={remove}>Delete contact</button>
          )}
        </div>
      )}

      {editing && (
        <ContactSheet title={`Edit ${contact.name}`} initial={contact} onSave={save} onClose={() => setEditing(false)} />
      )}
      {crediting && (
        <CreditNoteForm
          contact={contact}
          onCancel={() => setCrediting(false)}
          onDone={async (result) => {
            setCrediting(false);
            const number = result.creditNote.creditNoteNumber;
            if (result.emailed) {
              setNotice(null);
              toast(`${number} issued and emailed`);
            } else {
              toast(`${number} issued`);
              setNotice(`${number} wasn't emailed: ${SKIPPED[result.emailSkippedReason] || 'unknown reason'}.`);
            }
            await loadContact();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

export default function AdminBusinessContacts() {
  const [contacts, setContacts] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const toast = useToast();
  const { can } = useAuth();
  const managesBusiness = can('business', 'MANAGE');

  const load = useCallback(() => {
    setLoadError(false);
    return bookingApi.getBusinessContacts()
      .then(res => setContacts(res.data))
      .catch(() => setLoadError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (data) => {
    await bookingApi.createBusinessContact(data);
    setAdding(false);
    toast('Contact added');
    load();
  };

  let list;
  if (loadError && !contacts) list = <ErrorState title="Couldn't load business contacts" onRetry={load} />;
  else if (!contacts) list = <Skeleton rows={3} label="Loading contacts…" />;
  else if (contacts.length === 0) {
    list = (
      <EmptyState
        icon={BuildingOffice2Icon}
        title="No business contacts yet"
        text="Add the clubs and businesses you send invoices to."
        action={managesBusiness ? <button type="button" className="bk-btn bk-btn--primary" onClick={() => setAdding(true)}>Add contact</button> : undefined}
      />
    );
  } else {
    list = (
      <div className="inv-list">
        {contacts.map(c => (
          <React.Fragment key={c.id}>
            <button type="button" className="inv-row" aria-expanded={openId === c.id} onClick={() => setOpenId(openId === c.id ? null : c.id)}>
              <span className="inv-row__main">
                <span className="inv-row__name">{c.name}</span>
                <span className="inv-row__sub">{c.contactEmail || 'No email'}{c.creditBalance > 0 && ` · ${formatPence(c.creditBalance)} on account`}</span>
              </span>
              <span className="inv-muted inv-small">{c.invoiceCount} {c.invoiceCount === 1 ? 'invoice' : 'invoices'}</span>
            </button>
            {openId === c.id && (
              <ContactDetail
                id={c.id}
                onChanged={(deleted) => { if (deleted) setOpenId(null); load(); }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="bk-page bk-page--wide inv-page">
      <PageHeader
        back={{ to: '/admin-hub', label: 'Admin' }}
        title="Business contacts"
        details="Other clubs and businesses you send invoices to."
        action={managesBusiness ? { label: 'Add contact', short: 'Add', onClick: () => setAdding(true) } : undefined}
      />
      <p className="inv-muted inv-small" style={{ margin: 0 }}>
        <Link className="bk-link" to="/booking/admin/invoices">Go to invoices</Link>
      </p>

      {list}

      {adding && <ContactSheet title="Add a contact" onSave={create} onClose={() => setAdding(false)} />}
    </div>
  );
}
